import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { findJewelryById, JewelryItem } from '../data/jewelryData.ts';

const JewelryDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [piece, setPiece] = useState<JewelryItem | null>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [slideDirection, setSlideDirection] = useState<'left' | 'right' | null>(null);
  const [isZooming, setIsZooming] = useState(false);
  const [zoomPosition, setZoomPosition] = useState({ x: 0, y: 0 });
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const imageRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const touchStartX = useRef<number>(0);
  const touchEndX = useRef<number>(0);

  useEffect(() => {
    if (id) {
      const data = findJewelryById(id);
      if (data) {
        // Ordena as imagens alfabeticamente pelo nome do arquivo (1.jpg, 2.jpg, ...)
        const sortedImages = [...data.images].sort((a, b) => {
          const numA = parseInt(a.substring(a.lastIndexOf('/') + 1, a.lastIndexOf('.')));
          const numB = parseInt(b.substring(b.lastIndexOf('/') + 1, b.lastIndexOf('.')));
          return numA - numB;
        });
        setPiece({ ...data, images: sortedImages });
        setCurrentImageIndex(0); // Reseta para a primeira imagem ao carregar nova peça
      } else {
        setPiece(null);
      }
    }
  }, [id]);

  // Atualizar estado mobile quando a tela redimensionar
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  if (!piece) {
    return (
      <div className="container py-16 text-center">
        <h1 className="text-3xl font-bold mb-6">Peça não encontrada</h1>
        <p className="mb-8">A peça que você está procurando não existe em nosso catálogo.</p>
        <Link to="/catalogo" className="btn btn-primary">
          Voltar ao Catálogo
        </Link>
      </div>
    );
  }

  const handleThumbnailClick = (index: number) => {
    if (index === currentImageIndex) return;
    
    // Determina a direção da animação
    const direction = index > currentImageIndex ? 'left' : 'right';
    setSlideDirection(direction);
    
    // Define a próxima imagem como variável CSS
    if (containerRef.current) {
      containerRef.current.style.setProperty('--next-image', `url(${piece.images[index]})`);
    }
    
    // Remove a classe de animação e atualiza a imagem após a transição
    setTimeout(() => {
      setCurrentImageIndex(index);
      setSlideDirection(null);
      if (containerRef.current) {
        containerRef.current.style.removeProperty('--next-image');
      }
    }, 400);
  };

  const handlePrevImage = () => {
    const prevIndex = (currentImageIndex - 1 + piece.images.length) % piece.images.length;
    handleThumbnailClick(prevIndex);
  };

  const handleNextImage = () => {
    const nextIndex = (currentImageIndex + 1) % piece.images.length;
    handleThumbnailClick(nextIndex);
  };

  // Funções para manipular gestos touch
  const handleTouchStart = (e: React.TouchEvent) => {
    if (!isMobile) return;
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isMobile) return;
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (!isMobile) return;
    
    const touchDiff = touchStartX.current - touchEndX.current;
    const minSwipeDistance = 50; // Distância mínima para considerar um swipe
    
    if (Math.abs(touchDiff) > minSwipeDistance) {
      if (touchDiff > 0) {
        // Swipe para a esquerda - próxima imagem
        handleNextImage();
      } else {
        // Swipe para a direita - imagem anterior
        handlePrevImage();
      }
    }
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!containerRef.current || isMobile) return;
    
    const rect = containerRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    
    setZoomPosition({ x, y });
  };

  const handleMouseEnter = () => {
    if (!isMobile) {
      setIsZooming(true);
    }
  };

  const handleMouseLeave = () => {
    if (!isMobile) {
      setIsZooming(false);
    }
  };

  return (
    <div className="piece-detail-page py-16 bg-white">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
          {/* Coluna da Galeria de Imagens */}
          <div className="image-gallery-section">
            {/* Imagem Grande em Destaque */}
            <div 
              ref={containerRef}
              className={`featured-image-container mb-4 relative overflow-hidden rounded-lg aspect-square bg-white ${!isMobile ? 'cursor-crosshair' : ''}`}
              onMouseMove={handleMouseMove}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              <div className={`image-slide-container ${slideDirection ? `slide-${slideDirection}` : ''}`}>
                <img
                  ref={imageRef}
                  src={piece.images[currentImageIndex]}
                  alt={`${piece.name} - Imagem ${currentImageIndex + 1}`}
                  className="w-full h-full object-contain transition-all duration-300 ease-in-out"
                  key={currentImageIndex}
                  onMouseMove={handleMouseMove}
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                />
              </div>
              
              {/* Botões de Navegação */}
              {piece.images.length > 1 && (
                <>
                  <button
                    onClick={handlePrevImage}
                    className="absolute left-2 top-1/2 -translate-y-1/2 bg-azul-majestoso text-white p-2 rounded-full shadow-lg hover:bg-azul-mitologico transition-colors duration-200 z-10"
                    aria-label="Imagem anterior"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  <button
                    onClick={handleNextImage}
                    className="absolute right-2 top-1/2 -translate-y-1/2 bg-azul-majestoso text-white p-2 rounded-full shadow-lg hover:bg-azul-mitologico transition-colors duration-200 z-10"
                    aria-label="Próxima imagem"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </>
              )}

              {/* Círculo de Zoom */}
              {isZooming && (
                <div 
                  className="zoom-circle"
                  style={{
                    left: `${zoomPosition.x}%`,
                    top: `${zoomPosition.y}%`,
                    backgroundImage: `url(${piece.images[currentImageIndex]})`,
                    backgroundPosition: `${zoomPosition.x}% ${zoomPosition.y}%`
                  }}
                />
              )}
            </div>

            {/* Carrossel de Miniaturas */}
            <div className="thumbnail-carousel flex space-x-3 overflow-x-auto p-4 bg-white rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300">
              {piece.images.map((image, index) => (
                <button
                  key={index}
                  onClick={() => handleThumbnailClick(index)}
                  className={`thumbnail-item flex-shrink-0 w-20 h-20 rounded-md overflow-hidden border-2 transition-all duration-200 ${ 
                    currentImageIndex === index ? 'border-azul-majestoso scale-105' : 'border-transparent hover:border-azul-mitologico'
                  }`}
                  aria-label={`Ver imagem ${index + 1}`}
                >
                  <img
                    src={image}
                    alt={`${piece.name} - Miniatura ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          </div>

          {/* Coluna de Descrição */}
          <div className="description-section">
            <h1 className="text-3xl md:text-4xl font-bold text-azul-majestoso mb-4">
              {piece.name}
            </h1>
            
            <p className="text-gray-700 mb-6 leading-relaxed">
              {piece.fullDescription}
            </p>

            <div className="space-y-3 text-gray-600">
              <div className="flex items-center">
                <span className="font-semibold w-32">Material:</span>
                <span>{piece.material}</span>
              </div>
              {piece.category === 'aneis' && piece.size && (
                <div className="flex items-center">
                  <span className="font-semibold w-32">Tamanho:</span>
                  <span>{piece.size}</span>
                </div>
              )}
              <div className="flex items-center">
                <span className="font-semibold w-32">Peso:</span>
                <span>{piece.weight}</span>
              </div>
              <div className="flex items-center">
                <span className="font-semibold w-32">Técnica:</span>
                <span>{piece.creationMethod}</span>
              </div>
              <div className="flex items-center">
                <span className="font-semibold w-32">Lançamento:</span>
                <span>{piece.releaseDate}</span>
              </div>
              <div className="flex items-center">
                <span className="font-semibold w-32">Criador:</span>
                <span>{piece.creator}</span>
              </div>
              {piece.collection && (
                <div className="flex items-center">
                  <span className="font-semibold w-32">Coleção:</span>
                  <Link 
                    to={`/colecao/colecao-entre-mundos`}
                    className="text-azul-majestoso hover:text-azul-mitologico transition-colors duration-200"
                  >
                    {piece.collection}
                  </Link>
                </div>
              )}
            </div>

            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/contato" className="btn btn-primary">
                Solicitar Informações
              </Link>
              <Link to="/catalogo" className="btn btn-primary">
                Voltar ao Catálogo
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JewelryDetail;

